/**
 * Floating Safari Builder Button
 * Provides quick access to the Custom Safari Builder
 */

import React, { useState, useEffect } from 'react';
import { SparklesIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../../contexts/ThemeContext';
import { useSafariBuilder } from '../../contexts/SafariBuilderContext';
import CustomSafariBuilder from './CustomSafariBuilder';
import SafariCart from './SafariCart';
import { customSafariService } from '../../services/customSafariService';
import { useAuth } from '../../contexts/AuthContext';

const SafariBuilderButton: React.FC = () => {
  const { actualTheme } = useTheme();
  const { isBuilderOpen, openBuilder, closeBuilder, isCartOpen, openCart, closeCart } = useSafariBuilder();
  const { user, isAuthenticated, refreshUser } = useAuth();
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    const updateCartCount = async () => {
      try {
        // Only show cart count for authenticated users
        if (user && isAuthenticated) {
          const cart = await customSafariService.getCart(user.id);
          setCartCount(cart.length);
        } else {
          setCartCount(0); // No user, no cart items
        }
      } catch (error) {
        console.error('Error updating cart count:', error);
        setCartCount(0);
      }
    };

    updateCartCount();

    // Listen for cart updates
    const handleCartUpdate = () => {
      updateCartCount();
    };

    window.addEventListener('cartUpdated', handleCartUpdate);
    window.addEventListener('storage', handleCartUpdate);

    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
      window.removeEventListener('storage', handleCartUpdate);
    };
  }, [user, isAuthenticated]);

  return (
    <>
      {/* Floating Action Buttons */}
      {/* Container has pointer-events-none so it doesn't block clicks on cards */}
      {/* Individual buttons have pointer-events-auto to be clickable */}
      <div
        className="hidden pointer-events-none"
        style={{ pointerEvents: 'none' }}
      >
        {/* Cart Button - Always show, but require auth */}
        <button
          onClick={async (e) => {
            e.stopPropagation();
            // Refresh user credentials before opening cart
            await refreshUser();
            // Cart will show auth modal if not authenticated
            openCart();
          }}
          className={`relative p-4 rounded-full shadow-lg transition-all hover:scale-110 pointer-events-auto ${actualTheme === 'dark'
            ? 'bg-primary-700 hover:bg-primary-600 text-white'
            : 'bg-primary-800 hover:bg-primary-700 text-white'
            }`}
          style={{ pointerEvents: 'auto' }}
          title="View Safari Cart"
        >
          <ShoppingCartIcon className="h-6 w-6" />
          {cartCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
              {cartCount > 9 ? '9+' : cartCount}
            </span>
          )}
        </button>

        {/* Builder Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            openBuilder();
          }}
          className={`p-4 rounded-full shadow-lg transition-all hover:scale-110 pointer-events-auto ${actualTheme === 'dark'
            ? 'bg-primary-800 hover:bg-primary-700 text-white'
            : 'bg-primary-800 hover:bg-primary-700 text-white'
            }`}
          style={{ pointerEvents: 'auto' }}
          title="Build Custom Safari"
        >
          <SparklesIcon className="h-6 w-6" />
        </button>
      </div>

      {/* Modals */}
      <CustomSafariBuilder
        isOpen={isBuilderOpen}
        onClose={closeBuilder}
      />
      <SafariCart
        isOpen={isCartOpen}
        onClose={closeCart}
        onOpenBuilder={(tours) => {
          closeCart();
          openBuilder(tours);
        }}
      />
    </>
  );
};

export default SafariBuilderButton;

